import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { fmtISODate } from "@/lib/dates";
import { Search, UserPlus, X, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const MGMT = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "administrator", "super_admin"];

const TABS = [
  { key: "basic", label: "Basic details" },
  { key: "contact", label: "Contact & medical" },
  { key: "statutory", label: "Statutory plan" },
  { key: "needs", label: "Needs & communication" },
  { key: "consent", label: "Consent & legal" },
  { key: "staff", label: "Assigned staff" },
];

const SERVICE_TYPES = ["Domiciliary Care", "Supported Living", "Day Service / Community Support"];
const MCA_OPTIONS = [
  ["has_capacity", "Has capacity"],
  ["lacks_capacity_specific", "Lacks capacity (specific decision)"],
  ["lacks_capacity_general", "Lacks capacity (broadly)"],
  ["assessment_pending", "MCA assessment pending"],
  ["child_subject_to_parental_responsibility", "Child — parental responsibility"],
];
const DOLS_OPTIONS = [
  ["not_applicable", "Not applicable"],
  ["under_assessment", "Under assessment"],
  ["authorised", "Authorised"],
  ["expired", "Expired"],
];

const emptyForm = {
  full_name: "", preferred_name: "", dob: "", gender: "", pronouns: "", nhs_number: "",
  address: { line1: "", city: "", postcode: "", lat: null, lng: null },
  service_types: [], status: "active",
  service_user_type: "adult", placing_authority: "",
  statutory_links: {
    la_care_plan_ref: "", la_placement_plan_ref: "",
    iro_name: "", iro_phone: "",
    social_worker_name: "", social_worker_phone: "", social_worker_email: "",
    personal_education_plan_ref: "", looked_after_review_date: "",
    care_act_plan_ref: "",
    lead_professional_name: "", lead_professional_phone: "",
  },
  primary_diagnosis: "", communication_needs: "", mobility_notes: "",
  cultural_needs: "", religion: "", dietary_requirements: "", language: "", interpreter_required: false,
  mca_status: "has_capacity", dols_status: "not_applicable", consent_records: [],
  emergency_contacts: [],
  gp_details: { name: "", practice: "", phone: "", out_of_hours: "" },
  next_of_kin: { name: "", relationship: "", phone: "", email: "" },
  assigned_worker_ids: [], family_member_ids: [], key_worker_id: "",
};

export default function ServiceUsers() {
  const { user } = useAuth();
  const isMgmt = user && MGMT.includes(user.role);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null); // null | "new" | suId

  const load = () => api.get("/service-users").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((su) =>
    !search || su.full_name?.toLowerCase().includes(search.toLowerCase()) || su.preferred_name?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Service users</h1>
          <p className="text-sm text-slate-600">Person-centred records for everyone in your care.</p>
        </div>
        {isMgmt && (
          <button
            onClick={() => setDrawer("new")}
            data-testid="add-service-user-button"
            className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]"
          >
            <UserPlus className="h-4 w-4" /> Add service user
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
        <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="service-user-search-input"
            className="w-full bg-transparent outline-none text-sm"
          />
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid="service-users-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Service type</th>
                <th className="text-left py-2 px-3">Profile</th>
                <th className="text-left py-2 px-3">Key worker</th>
                <th className="text-left py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((su) => (
                <tr key={su.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-3">
                    <Link to={`/service-users/${su.id}`} data-testid={`su-row-${su.id}`} className="font-medium text-slate-900 hover:text-[#0D7377]">
                      {su.full_name}
                    </Link>
                    {su.preferred_name && <p className="text-xs text-slate-500">"{su.preferred_name}"</p>}
                  </td>
                  <td className="py-3 px-3 text-slate-700">{(su.service_types || []).join(", ") || "—"}</td>
                  <td className="py-3 px-3">
                    <CompletionPill completion={su.completion} />
                  </td>
                  <td className="py-3 px-3 text-slate-700">{su.key_worker_id ? "Assigned" : "—"}</td>
                  <td className="py-3 px-3"><StatusBadge status={su.status || "active"} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-500">No service users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {drawer === "new" && (
        <ServiceUserDrawer onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />
      )}
    </div>
  );
}

function CompletionPill({ completion }) {
  if (!completion) return <span className="text-xs text-slate-400">—</span>;
  const pct = completion.percent_complete;
  if (pct === 100) {
    return <span data-testid="profile-complete-pill" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Complete</span>;
  }
  return (
    <span data-testid="profile-incomplete-pill" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
      <AlertCircle className="h-3 w-3" /> {pct}% — {completion.incomplete_tabs.length} tab{completion.incomplete_tabs.length === 1 ? "" : "s"} to finish
    </span>
  );
}

export function ServiceUserDrawer({ existingId = null, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [createdId, setCreatedId] = useState(existingId);
  const [staff, setStaff] = useState([]);
  const [completion, setCompletion] = useState(null);

  useEffect(() => {
    api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
    if (existingId) {
      api.get(`/service-users/${existingId}`).then(({ data }) => {
        const su = data.service_user || {};
        setForm({
          ...emptyForm,
          ...su,
          address: { ...emptyForm.address, ...(su.address || {}) },
          gp_details: { ...emptyForm.gp_details, ...(su.gp_details || {}) },
          next_of_kin: { ...emptyForm.next_of_kin, ...(su.next_of_kin || {}) },
          statutory_links: { ...emptyForm.statutory_links, ...(su.statutory_links || {}) },
        });
        setCompletion(su.completion);
      }).catch(() => {});
    }
  }, [existingId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...(f[parent] || {}), [k]: v } }));

  const saveTab = async () => {
    setSaving(true); setError("");
    try {
      let id = createdId;
      if (!id) {
        // Initial create — must include full_name
        if (!form.full_name?.trim()) {
          setError("Full name is required to create the record.");
          setTab("basic"); setSaving(false); return;
        }
        const { data } = await api.post("/service-users", form);
        id = data.service_user.id;
        setCreatedId(id);
        setCompletion(data.service_user.completion);
      } else {
        // Patch only the current tab's relevant fields
        const patch = pickTabFields(form, tab);
        const { data } = await api.patch(`/service-users/${id}`, patch);
        setCompletion(data.service_user.completion);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  const finishAndClose = async () => {
    await saveTab();
    if (onSaved) onSaved(createdId);
  };

  const eligibleStaff = staff.filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role));
  const familyAccounts = staff.filter((s) => s.role === "family_member");

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="service-user-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{createdId ? "Edit service user" : "New service user"}</h2>
            <p className="text-xs text-slate-500">Save each tab independently. Record is active once Tab 1 is saved.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded" data-testid="su-drawer-close"><X className="h-5 w-5" /></button>
        </header>

        {completion && (
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-700">Profile completion</span>
              <span className="font-semibold text-slate-900" data-testid="su-completion-pct">{completion.percent_complete}%</span>
            </div>
            <div className="mt-1 h-1.5 bg-slate-200 rounded">
              <div className="h-1.5 bg-[#0D7377] rounded" style={{ width: `${completion.percent_complete}%` }} />
            </div>
            {completion.incomplete_tabs.length > 0 && (
              <p className="mt-1 text-xs text-amber-700" data-testid="su-incomplete-banner">Incomplete tabs: {completion.incomplete_tabs.join(", ")}</p>
            )}
          </div>
        )}

        <nav className="border-b border-slate-200 flex gap-1 overflow-x-auto px-3" role="tablist">
          {TABS.map((t) => {
            const tabComplete = completion?.by_tab?.[t.key]?.complete;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`su-tab-${t.key}`}
                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "text-[#0D7377] border-[#0D7377]" : "text-slate-600 border-transparent hover:text-slate-900"}`}>
                {t.label} {completion && (tabComplete ? "✓" : "•")}
              </button>
            );
          })}
        </nav>

        <div className="p-6 space-y-4">
          {tab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="su-tab-basic-content">
              <Input label="Full name *" value={form.full_name} onChange={(v) => setField("full_name", v)} testid="su-full-name-input" />
              <Input label="Preferred name" value={form.preferred_name} onChange={(v) => setField("preferred_name", v)} />
              <Input label="Date of birth" type="date" value={form.dob || ""} onChange={(v) => setField("dob", v)} testid="su-dob-input" />
              <Select label="Gender" value={form.gender} options={["", "Female", "Male", "Non-binary", "Prefer not to say"]} onChange={(v) => setField("gender", v)} />
              <Input label="Pronouns" value={form.pronouns} onChange={(v) => setField("pronouns", v)} />
              <Input label="NHS number" value={form.nhs_number} onChange={(v) => setField("nhs_number", v)} />
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input label="Address line" value={form.address.line1} onChange={(v) => setNested("address", "line1", v)} />
                <Input label="City" value={form.address.city} onChange={(v) => setNested("address", "city", v)} />
                <Input label="Postcode" value={form.address.postcode} onChange={(v) => setNested("address", "postcode", v)} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Service type *</label>
                <div className="flex flex-wrap gap-2" data-testid="su-service-types">
                  {SERVICE_TYPES.map((st) => (
                    <label key={st} className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-md cursor-pointer">
                      <input type="checkbox" checked={form.service_types.includes(st)} onChange={(e) => {
                        const next = e.target.checked ? [...form.service_types, st] : form.service_types.filter((x) => x !== st);
                        setField("service_types", next);
                      }} />
                      {st}
                    </label>
                  ))}
                </div>
              </div>
              <Select label="Status" value={form.status} options={["active", "on_hold", "discharged", "deceased"]} onChange={(v) => setField("status", v)} />
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">Person type *</label>
                <div className="flex gap-2" data-testid="su-type-toggle">
                  {[["adult", "Adult"], ["child", "Child / Young person"]].map(([k, lbl]) => (
                    <label key={k} className={`text-sm inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md cursor-pointer ${form.service_user_type === k ? "bg-[#0D7377] text-white border-[#0D7377]" : "border-slate-300"}`}>
                      <input type="radio" name="su_type" className="sr-only" checked={form.service_user_type === k} onChange={() => setField("service_user_type", k)} data-testid={`su-type-${k}`} />
                      {lbl}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Switches the Statutory Plan tab between CPPCR 2010 (children) and Care Act 2014 s25 (adults).</p>
              </div>
            </div>
          )}

          {tab === "statutory" && (
            <div className="space-y-4" data-testid="su-tab-statutory-content">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>References only.</strong> The statutory plan itself is held by the placing authority. Store the references here so staff can find it quickly.
              </div>
              {form.service_user_type === "child" ? (
                <fieldset className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Child / Young person — CPPCR 2010</legend>
                  <Input label="Placing authority" value={form.placing_authority} onChange={(v) => setField("placing_authority", v)} testid="su-placing-authority" />
                  <Input label="LA Care Plan reference" value={form.statutory_links.la_care_plan_ref} onChange={(v) => setNested("statutory_links", "la_care_plan_ref", v)} testid="su-la-careplan-ref" />
                  <Input label="LA Placement Plan reference" value={form.statutory_links.la_placement_plan_ref} onChange={(v) => setNested("statutory_links", "la_placement_plan_ref", v)} />
                  <Input label="Personal Education Plan ref" value={form.statutory_links.personal_education_plan_ref} onChange={(v) => setNested("statutory_links", "personal_education_plan_ref", v)} />
                  <Input label="Independent Reviewing Officer — name" value={form.statutory_links.iro_name} onChange={(v) => setNested("statutory_links", "iro_name", v)} testid="su-iro-name" />
                  <Input label="IRO — phone" value={form.statutory_links.iro_phone} onChange={(v) => setNested("statutory_links", "iro_phone", v)} />
                  <Input label="Social worker — name" value={form.statutory_links.social_worker_name} onChange={(v) => setNested("statutory_links", "social_worker_name", v)} testid="su-sw-name" />
                  <Input label="Social worker — phone" value={form.statutory_links.social_worker_phone} onChange={(v) => setNested("statutory_links", "social_worker_phone", v)} />
                  <Input label="Social worker — email" value={form.statutory_links.social_worker_email} onChange={(v) => setNested("statutory_links", "social_worker_email", v)} />
                  <Input label="Last LAC review date" type="date" value={fmtISODate(form.statutory_links.looked_after_review_date)} onChange={(v) => setNested("statutory_links", "looked_after_review_date", v)} />
                </fieldset>
              ) : (
                <fieldset className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Adult — Care Act 2014 s25</legend>
                  <Input label="LA Care Act plan reference" value={form.statutory_links.care_act_plan_ref} onChange={(v) => setNested("statutory_links", "care_act_plan_ref", v)} testid="su-careact-ref" />
                  <Input label="Lead professional — name" value={form.statutory_links.lead_professional_name} onChange={(v) => setNested("statutory_links", "lead_professional_name", v)} />
                  <Input label="Lead professional — phone" value={form.statutory_links.lead_professional_phone} onChange={(v) => setNested("statutory_links", "lead_professional_phone", v)} />
                </fieldset>
              )}
            </div>
          )}

          {tab === "contact" && (
            <div className="space-y-4" data-testid="su-tab-contact-content">
              <fieldset className="border border-slate-200 rounded-md p-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Emergency contacts</legend>
                <ContactList list={form.emergency_contacts} onChange={(v) => setField("emergency_contacts", v)} testid="su-emergency-contacts" />
              </fieldset>
              <fieldset className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">GP details</legend>
                <Input label="GP name" value={form.gp_details.name} onChange={(v) => setNested("gp_details", "name", v)} testid="su-gp-name" />
                <Input label="Practice" value={form.gp_details.practice} onChange={(v) => setNested("gp_details", "practice", v)} />
                <Input label="Phone" value={form.gp_details.phone} onChange={(v) => setNested("gp_details", "phone", v)} />
                <Input label="Out-of-hours" value={form.gp_details.out_of_hours} onChange={(v) => setNested("gp_details", "out_of_hours", v)} />
              </fieldset>
              <fieldset className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Next of kin</legend>
                <Input label="Name" value={form.next_of_kin.name} onChange={(v) => setNested("next_of_kin", "name", v)} />
                <Input label="Relationship" value={form.next_of_kin.relationship} onChange={(v) => setNested("next_of_kin", "relationship", v)} />
                <Input label="Phone" value={form.next_of_kin.phone} onChange={(v) => setNested("next_of_kin", "phone", v)} />
                <Input label="Email" value={form.next_of_kin.email} onChange={(v) => setNested("next_of_kin", "email", v)} />
              </fieldset>
            </div>
          )}

          {tab === "needs" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="su-tab-needs-content">
              <TextArea label="Primary diagnosis / presenting needs *" value={form.primary_diagnosis} onChange={(v) => setField("primary_diagnosis", v)} testid="su-primary-diagnosis" cols={2} rows={3} />
              <TextArea label="Communication needs *" hint="Makaton, PECS, Easy Read, verbal, non-verbal, etc." value={form.communication_needs} onChange={(v) => setField("communication_needs", v)} testid="su-communication-needs" cols={2} rows={3} />
              <TextArea label="Mobility & physical health" value={form.mobility_notes} onChange={(v) => setField("mobility_notes", v)} cols={2} rows={2} />
              <Input label="Religion" value={form.religion} onChange={(v) => setField("religion", v)} />
              <Input label="Cultural needs" value={form.cultural_needs} onChange={(v) => setField("cultural_needs", v)} />
              <Input label="Dietary requirements" value={form.dietary_requirements} onChange={(v) => setField("dietary_requirements", v)} />
              <Input label="Language" value={form.language} onChange={(v) => setField("language", v)} />
              <label className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-2 px-1 self-end">
                <input type="checkbox" checked={!!form.interpreter_required} onChange={(e) => setField("interpreter_required", e.target.checked)} />
                Interpreter required
              </label>
            </div>
          )}

          {tab === "consent" && (
            <div className="space-y-4" data-testid="su-tab-consent-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select label="Mental Capacity status *" value={form.mca_status} options={MCA_OPTIONS.map(([v]) => v)} labelMap={Object.fromEntries(MCA_OPTIONS)} onChange={(v) => setField("mca_status", v)} testid="su-mca-status" />
                <Select label="DoLS status *" value={form.dols_status} options={DOLS_OPTIONS.map(([v]) => v)} labelMap={Object.fromEntries(DOLS_OPTIONS)} onChange={(v) => setField("dols_status", v)} testid="su-dols-status" />
              </div>
              <fieldset className="border border-slate-200 rounded-md p-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Consent records</legend>
                <ConsentList list={form.consent_records} onChange={(v) => setField("consent_records", v)} testid="su-consent-records" />
              </fieldset>
            </div>
          )}

          {tab === "staff" && (
            <div className="space-y-4" data-testid="su-tab-staff-content">
              <fieldset className="border border-slate-200 rounded-md p-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Assigned care workers *</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                  {eligibleStaff.map((s) => (
                    <label key={s.id} className="text-sm inline-flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded">
                      <input type="checkbox" checked={form.assigned_worker_ids.includes(s.id)} onChange={(e) => {
                        const next = e.target.checked ? [...form.assigned_worker_ids, s.id] : form.assigned_worker_ids.filter((x) => x !== s.id);
                        setField("assigned_worker_ids", next);
                      }} />
                      <span>{s.full_name} <span className="text-xs text-slate-500">({s.role.replace(/_/g, " ")})</span></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <Select label="Key Worker *" value={form.key_worker_id || ""} options={["", ...form.assigned_worker_ids]} labelMap={Object.fromEntries(eligibleStaff.map((s) => [s.id, s.full_name]))} onChange={(v) => setField("key_worker_id", v)} testid="su-key-worker-select" />
              {familyAccounts.length > 0 && (
                <fieldset className="border border-slate-200 rounded-md p-3">
                  <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Linked family / advocate accounts</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {familyAccounts.map((s) => (
                      <label key={s.id} className="text-sm inline-flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded">
                        <input type="checkbox" checked={form.family_member_ids.includes(s.id)} onChange={(e) => {
                          const next = e.target.checked ? [...form.family_member_ids, s.id] : form.family_member_ids.filter((x) => x !== s.id);
                          setField("family_member_ids", next);
                        }} />
                        {s.full_name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

          <footer className="flex items-center justify-between pt-2 border-t border-slate-200">
            <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Close</button>
            <div className="flex gap-2">
              <button onClick={saveTab} disabled={saving} data-testid="su-save-tab-button" className="px-4 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium text-sm">
                {saving ? "Saving…" : "Save this tab"}
              </button>
              <button onClick={finishAndClose} disabled={saving} className="px-4 py-2 border border-[#0D7377] text-[#0D7377] rounded-md font-medium text-sm" data-testid="su-finish-button">Save & close</button>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

function pickTabFields(form, tab) {
  const map = {
    basic: ["full_name", "preferred_name", "dob", "gender", "pronouns", "nhs_number", "address", "service_types", "status", "service_user_type"],
    statutory: ["placing_authority", "statutory_links"],
    contact: ["emergency_contacts", "gp_details", "next_of_kin"],
    needs: ["primary_diagnosis", "communication_needs", "mobility_notes", "religion", "cultural_needs", "dietary_requirements", "language", "interpreter_required"],
    consent: ["mca_status", "dols_status", "consent_records"],
    staff: ["assigned_worker_ids", "family_member_ids", "key_worker_id"],
  };
  const out = {};
  for (const f of map[tab] || []) out[f] = form[f];
  return out;
}

function Input({ label, value, onChange, type = "text", testid, hint }) {
  return (
    <label className="block text-sm">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function TextArea({ label, value, onChange, testid, cols = 1, rows = 3, hint }) {
  return (
    <label className={`block text-sm ${cols === 2 ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function Select({ label, value, options, onChange, testid, labelMap = {} }) {
  return (
    <label className="block text-sm">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
        {options.map((o) => <option key={o} value={o}>{labelMap[o] || o || "—"}</option>)}
      </select>
    </label>
  );
}

function ContactList({ list, onChange, testid }) {
  const add = () => onChange([...(list || []), { name: "", relationship: "", phone: "", email: "" }]);
  const set = (i, k, v) => onChange((list || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const rm = (i) => onChange((list || []).filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {(list || []).map((c, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <Input label="Name" value={c.name} onChange={(v) => set(i, "name", v)} />
          <Input label="Relationship" value={c.relationship} onChange={(v) => set(i, "relationship", v)} />
          <Input label="Phone" value={c.phone} onChange={(v) => set(i, "phone", v)} />
          <Input label="Email" value={c.email} onChange={(v) => set(i, "email", v)} />
          <button onClick={() => rm(i)} className="px-2 py-2 text-xs text-red-600 border border-red-200 rounded">Remove</button>
        </div>
      ))}
      <button onClick={add} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md" data-testid={`${testid}-add`}>+ Add contact</button>
    </div>
  );
}

function ConsentList({ list, onChange, testid }) {
  const add = () => onChange([...(list || []), { what: "", who_consented: "", when: fmtISODate(new Date()) }]);
  const set = (i, k, v) => onChange((list || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const rm = (i) => onChange((list || []).filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {(list || []).map((c, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <Input label="What was consented to" value={c.what} onChange={(v) => set(i, "what", v)} />
          <Input label="Consented by" value={c.who_consented} onChange={(v) => set(i, "who_consented", v)} />
          <Input label="Date" type="date" value={c.when} onChange={(v) => set(i, "when", v)} />
          <button onClick={() => rm(i)} className="px-2 py-2 text-xs text-red-600 border border-red-200 rounded">Remove</button>
        </div>
      ))}
      <button onClick={add} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md" data-testid={`${testid}-add`}>+ Add consent record</button>
    </div>
  );
}
